/**
 * Snel-kenteken - shop behaviour: filter panel, card swipe arrows, favourites.
 * Ported from the Mowi Jewellery H&M-style build (own code). No dependencies.
 * Add-to-bag / PDP / bag behaviours land as those templates are built.
 */
( () => {
	'use strict';

	const $ = ( sel, ctx = document ) => ctx.querySelector( sel );
	const $$ = ( sel, ctx = document ) => Array.from( ctx.querySelectorAll( sel ) );
	const snel = window.snel || {};

	/* ------------------------------------------------------------------
	 * Filter panel (collection pages)
	 * ---------------------------------------------------------------- */
	const plp = $( '[data-plp]' );
	const filterPanel = $( '[data-filter-panel]' );
	const filterOpenBtns = $$( '[data-filter-open]' );

	function openFilters() {
		if ( ! filterPanel ) return;
		filterPanel.hidden = false;
		plp?.classList.add( 'is-filters-open' );
		filterOpenBtns.forEach( ( b ) => b.setAttribute( 'aria-expanded', 'true' ) );
		if ( window.matchMedia( '(max-width: 1023px)' ).matches ) {
			snel.showOverlay?.( 'filters' );
		}
	}

	function closeFilters() {
		if ( ! filterPanel || filterPanel.hidden ) return;
		filterPanel.hidden = true;
		plp?.classList.remove( 'is-filters-open' );
		filterOpenBtns.forEach( ( b ) => b.setAttribute( 'aria-expanded', 'false' ) );
		snel.hideOverlay?.( 'filters' );
	}

	filterOpenBtns.forEach( ( btn ) => btn.addEventListener( 'click', openFilters ) );
	$( '[data-filter-close]' )?.addEventListener( 'click', closeFilters );
	document.addEventListener( 'snel:overlay-click', closeFilters );
	$( '[data-overlay]' )?.addEventListener( 'click', closeFilters );

	document.addEventListener( 'keydown', ( e ) => {
		if ( e.key === 'Escape' ) closeFilters();
	} );

	/* Price range dual slider: keep the two thumbs from crossing and update the
	   filled track + visible values as they move. */
	$$( '[data-price-range]' ).forEach( ( wrap ) => {
		const min = $( '[data-price-min]', wrap );
		const max = $( '[data-price-max]', wrap );
		const fromLabel = $( '[data-price-from]', wrap );
		const toLabel = $( '[data-price-to]', wrap );
		if ( ! min || ! max ) return;

		function formatEuro( n ) {
			return '€' + n.toFixed( 2 ).replace( '.', ',' );
		}

		function sync() {
			let a = Number( min.value );
			let b = Number( max.value );
			if ( a > b ) {
				[ a, b ] = [ b, a ];
			}
			const range = Number( min.max ) || 1;
			wrap.style.setProperty( '--from', ( a / range ) * 100 + '%' );
			wrap.style.setProperty( '--to', ( b / range ) * 100 + '%' );
			if ( fromLabel ) fromLabel.textContent = formatEuro( a );
			if ( toLabel ) toLabel.textContent = formatEuro( b );
		}

		min.addEventListener( 'input', sync );
		max.addEventListener( 'input', sync );
		sync();

		/* Shopify's price filter silently ignores the whole gte/lte pair when
		   gte is submitted as "0" (the slider's untouched default) - strip a
		   bound from the submission when it's still sitting at its default
		   (min at 0, max at the slider's own max), so only a bound the
		   shopper actually moved gets sent. */
		const form = wrap.closest( 'form' );
		form?.addEventListener( 'submit', () => {
			min.disabled = Number( min.value ) <= 0;
			max.disabled = Number( max.value ) >= Number( max.max );
		} );
	} );

	/* ------------------------------------------------------------------
	 * Product card image swipe arrows
	 * ---------------------------------------------------------------- */
	$$( '.product-card--swipe' ).forEach( ( card ) => {
		const track = $( '[data-card-track]', card );
		const prev = $( '[data-card-prev]', card );
		const next = $( '[data-card-next]', card );
		if ( ! track ) return;

		function update() {
			const max = track.scrollWidth - track.clientWidth - 2;
			if ( prev ) prev.disabled = track.scrollLeft <= 2;
			if ( next ) next.disabled = track.scrollLeft >= max;
		}

		function step( dir ) {
			track.scrollBy( { left: dir * track.clientWidth, behavior: 'smooth' } );
		}

		prev?.addEventListener( 'click', ( e ) => {
			e.preventDefault();
			step( -1 );
		} );
		next?.addEventListener( 'click', ( e ) => {
			e.preventDefault();
			step( 1 );
		} );
		track.addEventListener( 'scroll', update, { passive: true } );
		update();
	} );

	/* ------------------------------------------------------------------
	 * Carousels (product recommendations, featured products)
	 * ---------------------------------------------------------------- */
	$$( '[data-carousel]' ).forEach( ( carousel ) => {
		const track = $( '[data-carousel-track]', carousel );
		const prev = $( '[data-carousel-prev]', carousel );
		const next = $( '[data-carousel-next]', carousel );
		if ( ! track ) return;

		function update() {
			const max = track.scrollWidth - track.clientWidth - 2;
			carousel.classList.toggle( 'is-static', max <= 2 );
			if ( prev ) prev.disabled = track.scrollLeft <= 2;
			if ( next ) next.disabled = track.scrollLeft >= max;
		}

		function step( dir ) {
			const card = track.firstElementChild;
			const w = card ? card.getBoundingClientRect().width + 2 : track.clientWidth;
			track.scrollBy( { left: dir * w * Math.max( 1, Math.floor( track.clientWidth / w ) ), behavior: 'smooth' } );
		}

		prev?.addEventListener( 'click', () => step( -1 ) );
		next?.addEventListener( 'click', () => step( 1 ) );
		track.addEventListener( 'scroll', update, { passive: true } );
		update();
	} );

	/* ------------------------------------------------------------------
	 * Product page: variant picker + add to bag (properties + add-ons)
	 * ---------------------------------------------------------------- */
	const pdp = $( '[data-pdp]' );
	if ( pdp ) {
		const variantsJson = $( '[data-product-json]', pdp );
		const variants = variantsJson ? JSON.parse( variantsJson.textContent ) : [];
		const imageMapJson = $( '[data-variant-image-map]', pdp );
		const variantImageMap = imageMapJson ? JSON.parse( imageMapJson.textContent ) : {};
		const variantInput = $( '[data-variant-id]', pdp );
		const priceEl = $( '[data-price]', pdp );
		const form = $( '[data-pdp-form]', pdp );

		// data-option-value is "index:value"; the value itself may contain ":".
		const optionValue = ( btn ) => btn.dataset.optionValue.split( ':' ).slice( 1 ).join( ':' );

		/* Selected option values, one per option index (0-based), read from the
		   currently-marked buttons so page load reflects the pre-selected variant.
		   Read from data-option-value, not from the button's text: a button does
		   not always say its whole value. A colour swatch carries the name in a
		   screen-reader span, and a size button shows only the height ("15 cm")
		   while the value is "15 cm hoog (ca. 100 cm breed) - wettelijk vereist".
		   Taking the text there put a string in `selected` that matches no
		   variant, so findVariant() returned nothing and reconcileOptions()
		   started guessing. */
		const selected = [];
		$$( '.pdp-option', pdp ).forEach( ( group ) => {
			const idx = Number( group.dataset.optionIndex );
			const current = $( '.pdp-option__btn.is-selected', group );
			selected[ idx ] = current ? optionValue( current ) : null;
		} );

		function findVariant() {
			return variants.find( ( v ) => v.options.every( ( val, i ) => val === selected[ i ] ) );
		}

		function formatMoney( cents ) {
			return '€' + ( cents / 100 ).toFixed( 2 ).replace( '.', ',' );
		}

		/* Show only the photo(s) tied to the selected variant (matches the old
		   theme's behaviour), falling back to showing every photo when a
		   variant has no image of its own. */
		function updateGalleryForVariant( variant ) {
			const items = $$( '.gallery__item[data-media-id]', pdp );
			if ( ! items.length || ! variant ) return;
			const imageId = variantImageMap[ String( variant.id ) ] || null;
			const matches = imageId ? items.filter( ( el ) => el.dataset.mediaId === imageId ) : [];
			const visible = matches.length ? matches : items;
			items.forEach( ( el ) => ( el.hidden = ! visible.includes( el ) ) );
		}

		// Sold out, or a consent box further down that hasn't been ticked yet.
		// A sold-out choice (the 7-13 verloopstekker, 2026-10-22) also says so on
		// the button and drops the "ships today" promise.
		function syncAddButton( variant ) {
			const addBtn = $( '[data-pdp-add-button]', pdp );
			if ( ! addBtn ) return;
			const consentInput = $( '[data-consent-input]', pdp );
			const soldOut = !! variant && ! variant.available;
			addBtn.disabled = soldOut || ( !! consentInput && ! consentInput.checked );
			const label = $( '[data-btn-text]', addBtn );
			if ( label && addBtn.dataset.soldOut && label.dataset.addText ) {
				label.textContent = soldOut ? addBtn.dataset.soldOut : label.dataset.addText;
			}
			const promise = $( '[data-verzendbelofte]', pdp );
			if ( promise ) promise.hidden = soldOut;
		}

		function updateForVariant( variant ) {
			if ( ! variant ) return;
			if ( variantInput ) variantInput.value = variant.id;
			if ( priceEl ) {
				if ( variant.compare_at_price && variant.compare_at_price > variant.price ) {
					priceEl.innerHTML = `<span class="price--sale">${ formatMoney( variant.price ) }</span> <span class="price--compare">${ formatMoney( variant.compare_at_price ) }</span>`;
				} else {
					priceEl.textContent = formatMoney( variant.price );
				}
			}
			syncAddButton( variant );
			updateGalleryForVariant( variant );
			pdp.dispatchEvent( new CustomEvent( 'snel:variant', { detail: variant } ) );
		}

		/* Not every combination of options exists (the oldtimer's "Motor" format
		   only comes with pressed letters). After a change, options that no longer
		   fit move to the closest real variant. A value is marked unavailable only
		   when it can't be combined with the options before it, so the first
		   option (e.g. the format) always stays clickable. */
		function reconcileOptions( changedIdx ) {
			if ( ! findVariant() ) {
				const matches = ( v ) => v.options.filter( ( val, i ) => val === selected[ i ] ).length + ( v.available ? 0.5 : 0 );
				const best = variants
					.filter( ( v ) => v.options[ changedIdx ] === selected[ changedIdx ] )
					.sort( ( a, b ) => matches( b ) - matches( a ) )[ 0 ];
				if ( best ) best.options.forEach( ( val, i ) => ( selected[ i ] = val ) );
			}
			$$( '.pdp-option', pdp ).forEach( ( group ) => {
				const idx = Number( group.dataset.optionIndex );
				$$( '.pdp-option__btn', group ).forEach( ( b ) => {
					const value = optionValue( b );
					b.classList.toggle( 'is-selected', value === selected[ idx ] );
					b.disabled = ! variants.some( ( v ) => v.options[ idx ] === value && v.options.slice( 0, idx ).every( ( val, i ) => val === selected[ i ] ) );
				} );
				// Colour swatches say nothing on their own, so the name follows the label.
				const chosen = $( '[data-option-chosen]', group );
				if ( chosen ) chosen.textContent = selected[ idx ] || '';
			} );
			syncUpgradeTiles();
		}

		/* An option shown as one upsell tile ("Opgelegde letters"): the tile
		   follows whatever variant exists for the format chosen now, so it is
		   hidden on a format without a raised-letter variant (Motor, Amerikaans;
		   Sal 2026-10-22: hide it, don't grey it out), and always shows that
		   format's own surcharge. */
		const upgradeSource = ( group ) =>
			$$( `.pdp-option[data-option-index="${ group.dataset.optionIndex }"]`, pdp ).find( ( el ) => ! el.hasAttribute( 'data-upgrade' ) );

		function syncUpgradeTiles() {
			$$( '[data-upgrade]', pdp ).forEach( ( group ) => {
				const idx = Number( group.dataset.optionIndex );
				const source = upgradeSource( group );
				if ( ! source ) return;
				const input = $( '[data-upgrade-input]', group );
				if ( ! input ) return;
				const upgradeBtn = $$( '.pdp-option__btn', source ).find( ( b ) => optionValue( b ) === group.dataset.valueUpgrade );
				input.checked = selected[ idx ] === group.dataset.valueUpgrade;
				// Motor and Amerikaans have no raised-letter variant: no tile at all.
				input.disabled = ! upgradeBtn || upgradeBtn.disabled;
				group.hidden = input.disabled;
				const priceEl = $( '[data-upgrade-price]', group );
				if ( ! priceEl ) return;
				const forValue = ( value ) => variants.find( ( v ) => v.options.every( ( val, i ) => ( i === idx ? val === value : val === selected[ i ] ) ) );
				const base = forValue( group.dataset.valueBase );
				const upgrade = forValue( group.dataset.valueUpgrade );
				if ( base && upgrade ) priceEl.textContent = '+ ' + formatMoney( upgrade.price - base.price );
			} );
		}

		$$( '[data-upgrade]', pdp ).forEach( ( group ) => {
			const input = $( '[data-upgrade-input]', group );
			input?.addEventListener( 'change', () => {
				const source = upgradeSource( group );
				const value = input.checked ? group.dataset.valueUpgrade : group.dataset.valueBase;
				const btn = $$( '.pdp-option__btn', source ).find( ( b ) => optionValue( b ) === value );
				if ( btn && ! btn.classList.contains( 'is-selected' ) ) btn.click();
			} );
		} );

		$$( '.pdp-option__btn', pdp ).forEach( ( btn ) => {
			btn.addEventListener( 'click', () => {
				const idx = Number( btn.dataset.optionValue.split( ':' )[ 0 ] );
				selected[ idx ] = optionValue( btn );
				reconcileOptions( idx );
				updateForVariant( findVariant() );
			} );
		} );
		reconcileOptions( 0 );

		/* "Bevestiging" as two combinable checkboxes (gaatjes / schroeven): the
		   product still has one variant per combination, so a change just clicks
		   the matching hidden option button and the normal variant flow runs. */
		$$( '[data-combinable]', pdp ).forEach( ( group ) => {
			const source = $$( `.pdp-option[data-option-index="${ group.dataset.optionIndex }"]`, pdp ).find( ( el ) => ! el.hasAttribute( 'data-combinable' ) );
			if ( ! source ) return;
			function sync() {
				const holes = $( '[data-combo="holes"]', group )?.checked;
				const screws = $( '[data-combo="screws"]', group )?.checked;
				const value = holes && screws ? group.dataset.valueCombo : holes ? group.dataset.valueHoles : screws ? group.dataset.valueScrews : group.dataset.valueBase;
				const btn = $$( '.pdp-option__btn', source ).find( ( b ) => b.dataset.optionValue.split( ':' ).slice( 1 ).join( ':' ) === value );
				if ( btn && ! btn.classList.contains( 'is-selected' ) ) btn.click();
			}
			$$( 'input[type="checkbox"]', group ).forEach( ( cb ) => cb.addEventListener( 'change', sync ) );
		} );

		// Filter the gallery to the pre-selected variant on first render too,
		// not only after a click.
		updateGalleryForVariant( findVariant() );

		/* Add-ons sold in sizes (the chroomlijst, Sal 2026-10-15: the customer and
		   the shop both have to see which size is ordered). The tile follows the
		   plate chosen above: a motor plate takes the motor size, the model 18.2
		   plate (its own product, or the oldtimer's "Amerikaans 31x11 cm" choice)
		   the "Amerikaans" size, and an oldtimer car plate the size with the
		   same height and the nearest width (plate 31 x 19.5 -> lijst 31.5 x 19.5).
		   The cart and the order then carry the size as the variant title. */
		const addonHost = $( '[data-addon-host]', pdp )?.dataset.addonHost.toLowerCase() || '';
		const sizeOf = ( v ) => {
			const m = String( v ).match( /(\d+(?:[.,]\d+)?)\s*x\s*(\d+(?:[.,]\d+)?)/i );
			return m ? [ parseFloat( m[ 1 ].replace( ',', '.' ) ), parseFloat( m[ 2 ].replace( ',', '.' ) ) ] : null;
		};
		function pickAddonSize( sizes, variant ) {
			const values = ( variant?.options || [] ).map( ( v ) => String( v ).toLowerCase() );
			const has = ( re ) => sizes.find( ( s ) => re.test( s.title ) );
			if ( values.some( ( v ) => /motor/.test( v ) ) || ( ! values.some( sizeOf ) && /\bmotor\b/.test( addonHost ) ) ) return has( /motor/i );
			if ( /amerikaans/.test( addonHost ) || values.some( ( v ) => /amerika/.test( v ) ) ) return has( /amerika/i );
			const plate = values.map( sizeOf ).find( Boolean );
			if ( ! plate ) return null;
			let best = null;
			for ( const s of sizes ) {
				const size = sizeOf( s.title );
				if ( ! size || /motor|amerika/i.test( s.title ) || Math.abs( size[ 1 ] - plate[ 1 ] ) > 0.5 ) continue;
				if ( ! best || Math.abs( size[ 0 ] - plate[ 0 ] ) < Math.abs( sizeOf( best.title )[ 0 ] - plate[ 0 ] ) ) best = s;
			}
			return best;
		}
		function syncAddonSizes( variant ) {
			$$( '[data-addon-sized]', pdp ).forEach( ( tile ) => {
				const sizes = JSON.parse( $( '[data-addon-variants]', tile ).textContent );
				const pick = pickAddonSize( sizes, variant ) || sizes.find( ( s ) => s.available ) || sizes[ 0 ];
				const input = $( '[data-addon]', tile );
				input.value = pick.id;
				input.disabled = ! pick.available || tile.hasAttribute( 'data-addon-locked' );
				if ( ! pick.available ) input.checked = false;
				const label = $( '[data-addon-size]', tile );
				if ( label ) label.textContent = pick.title;
				if ( pick.zoom ) $( '.pdp-tile__box', tile ).style.setProperty( '--tile-zoom', pick.zoom );
				const img = $( '.pdp-tile__img', tile );
				if ( img && pick.image && img.dataset.src !== pick.image ) {
					img.removeAttribute( 'srcset' );
					img.dataset.src = pick.image;
					img.src = pick.image;
				}
			} );
		}
		pdp.addEventListener( 'snel:variant', ( e ) => syncAddonSizes( e.detail ) );

		/* The sister product link (custom.alternatief): opens the other plate
		   on the same colour and length where it has them (Blauw is a funplaat
		   colour only, then the length alone decides), and hands the typed text
		   over in sessionStorage - a name has no business in the URL and the
		   analytics behind it. plate-preview.js picks it up there. */
		const altLink = $( '[data-alt-link]', pdp );
		altLink?.addEventListener( 'click', () => {
			const sister = JSON.parse( $( '[data-alt-variants]', altLink ).textContent );
			const own = ( findVariant()?.options || selected ).map( ( v ) => String( v || '' ).toLowerCase() );
			const score = ( v ) => v.options.filter( ( val ) => own.includes( val.toLowerCase() ) ).length + ( v.available ? 0.5 : 0 );
			const best = sister.slice().sort( ( a, b ) => score( b ) - score( a ) )[ 0 ];
			const url = new URL( altLink.href, window.location.origin );
			if ( best && score( best ) >= 1 ) url.searchParams.set( 'variant', best.id );
			altLink.href = url.pathname + url.search;
			const text = $( '[data-personalisation-input]', pdp )?.value.trim();
			try {
				if ( text ) sessionStorage.setItem( 'snel:tekst', text );
				else sessionStorage.removeItem( 'snel:tekst' );
			} catch ( err ) {}
		} );
		syncAddonSizes( findVariant() || variants[ 0 ] );

		/* A note under an option (custom.optie_meldingen) shows while its value is
		   selected. A note that names a bigger size (data-size-up) follows the
		   typed kenteken as well: plate-preview.js reports whether it fits the
		   selected size (snel:plate-fit), and the note says so - or that the shop
		   presses it on the bigger size, with a button to choose that one. */
		const sizeLabel = ( v ) => {
			const m = String( v ).match( /(\d+(?:[.,]\d+)?)\s*x\s*(\d+(?:[.,]\d+)?)/i );
			return m ? `${ m[ 1 ].replace( '.', ',' ) } x ${ m[ 2 ].replace( '.', ',' ) } cm` : v;
		};
		let noteVariant = findVariant() || variants[ 0 ];
		let plateFit = { text: '', fits: null };
		function syncOptionNotes() {
			$$( '[data-option-note]', pdp ).forEach( ( note ) => {
				const value = noteVariant?.options?.[ note.closest( '[data-option-index]' ).dataset.optionIndex ] || '';
				note.hidden = ! value.includes( note.dataset.optionNote );
				const up = note.dataset.sizeUp;
				if ( ! up ) return;
				const state = plateFit.fits === null ? 'general' : plateFit.fits ? 'fits' : 'too-wide';
				$$( '[data-note-state]', note ).forEach( ( p ) => ( p.hidden = p.dataset.noteState !== state ) );
				$$( '[data-note-text]', note ).forEach( ( el ) => {
					el.textContent = el.dataset.noteText
						.replace( '[kenteken]', plateFit.text )
						.replace( '[maat]', sizeLabel( note.dataset.optionNote ) )
						.replace( '[groter]', sizeLabel( up ) );
				} );
			} );
		}
		pdp.addEventListener( 'snel:variant', ( e ) => {
			noteVariant = e.detail;
			syncOptionNotes();
		} );
		pdp.addEventListener( 'snel:plate-fit', ( e ) => {
			plateFit = e.detail;
			syncOptionNotes();
		} );
		pdp.addEventListener( 'click', ( e ) => {
			const btn = e.target.closest( '[data-size-switch]' );
			if ( ! btn ) return;
			const note = btn.closest( '[data-option-note]' );
			const index = note.closest( '[data-option-index]' ).dataset.optionIndex;
			$$( `[data-option-index="${ index }"] .pdp-option__btn`, pdp ).find( ( b ) => b.dataset.optionValue === `${ index }:${ note.dataset.sizeUp }` )?.click();
		} );

		/* Add to bag: main variant + properties, plus one item per checked
		   add-on, all in a single /cart/add.js call, then straight on to the cart
		   page - it shows what was added, the accessories that go with it and the
		   checkout button, so no toast is needed. */

		/* The form is novalidate (custom UI), so required/pattern on the kenteken
		   or name field are checked here. Also covers the floating phone button,
		   which is visible before anything has been typed. */
		const textInput = $( '[data-personalisation-input]', pdp );
		const textHint = textInput ? $( '.pdp-text-field__hint', textInput.closest( '.pdp-text-field' ) ) : null;
		if ( textHint ) textHint.dataset.defaultText = textHint.textContent;

		function setTextError( message ) {
			if ( ! textInput ) return;
			textInput.toggleAttribute( 'aria-invalid', !! message );
			if ( textHint ) {
				textHint.textContent = message || textHint.dataset.defaultText || '';
				textHint.classList.toggle( 'is-error', !! message );
			}
		}

		/* A plate that may be ordered blank (specs.blanco_toegestaan puts
		   [data-blank] under the add button): an empty required field is then
		   not an error but that warning, with its own confirm button. The first
		   click on "toevoegen" shows it and has the preview draw the plate empty
		   (is-blank on the wrap, announced with snel:blank); the confirm button
		   adds the plate with "Blanco plaat (geen kenteken)" as the Kenteken
		   property, so the order says what to press. Typing takes it away. */
		const blankBox = $( '[data-blank]', pdp );
		const blankBtn = blankBox ? $( '[data-blank-confirm]', blankBox ) : null;
		const plateWrap = $( '[data-plate-preview]', pdp );
		let blankConfirmed = false;

		function showBlank( on ) {
			if ( ! blankBox ) return;
			blankBox.hidden = ! on;
			plateWrap?.classList.toggle( 'is-blank', on );
			pdp.dispatchEvent( new CustomEvent( 'snel:blank', { detail: on } ) );
		}

		/* A funplaat or name plate may not carry a real registration: pressed with
		   one it reads as a specific vehicle's plate to a camera, a barrier or an
		   officer at a glance, which is not what the shop makes. Every Dutch
		   format is six characters mixing letters and digits, so a name can never
		   collide with one - see scripts/lib/kenteken.mjs, which holds the twelve
		   sidecodes this expression is generated from and checks that the two have
		   not drifted apart. Only set on the novelty plates: the legal plates need
		   a real registration, and a parkeerbord may well name one. */
		const KENTEKEN = /^(?:[A-Z]{2}\d{4}|\d{4}[A-Z]{2}|\d{2}[A-Z]{2}\d{2}|[A-Z]{2}\d{2}[A-Z]{2}|[A-Z]{4}\d{2}|\d{2}[A-Z]{4}|\d{2}[A-Z]{3}\d|\d[A-Z]{3}\d{2}|[A-Z]{2}\d{3}[A-Z]|[A-Z]\d{3}[A-Z]{2}|[A-Z]{3}\d{2}[A-Z]|[A-Z]\d{2}[A-Z]{3}|\d{3}[A-Z]{2}\d)$/;

		/* Reads as a plate without being one of the shapes above - "44-SG-3". No
		   five-character registration exists, so no vehicle can be impersonated
		   with it, but it is still meant to be taken for a plate at a glance.
		   Two separate numbers and no run of letters long enough to be a word is
		   what tells it from a present: "NR 1 OMA" has one number, "MAMA 4EVER"
		   has a word. */
		function plateLike( bare ) {
			if ( bare.length < 5 || bare.length > 8 ) return false;
			const runs = bare.match( /[A-Z]+|\d+/g ) || [];
			if ( runs.length < 3 ) return false;
			if ( ! /\d/.test( bare ) || ! /[A-Z]/.test( bare ) ) return false;
			if ( runs.filter( ( r ) => /\d/.test( r ) ).length < 2 ) return false;
			return runs.every( ( r ) => /\d/.test( r ) || r.length <= 3 );
		}

		/* Shape alone is not enough to refuse on. "LISA 22" for a birthday is an
		   ordinary order, and it is also the shape of sidecode 5 (LI-SA-22). What
		   separates the two is how the characters run: a present is a word and a
		   number, a plate weaves letters and digits together, so "12-ABC-3"
		   breaks into three runs and "LISA 22" into two. Three or more runs is a
		   plate, full stop. Two runs is a present unless the RDW's open register
		   says a vehicle really carries it - that is the case worth refusing,
		   because that is the plate someone could be impersonated with. */
		const RDW = 'https://opendata.rdw.nl/resource/m9d7-ebf2.json';
		const rdwAnswers = new Map();

		function bareText( value ) {
			return String( value ).toUpperCase().replace( /[\s-]/g, '' );
		}

		async function onRealVehicle( bare ) {
			if ( rdwAnswers.has( bare ) ) return rdwAnswers.get( bare );
			let answer;
			try {
				const res = await fetch( `${ RDW }?kenteken=${ encodeURIComponent( bare ) }&$select=kenteken&$limit=1`, {
					signal: AbortSignal.timeout( 6000 ),
				} );
				if ( res.ok ) answer = ( await res.json() ).length > 0;
			} catch ( err ) {}
			// Left undefined when the register could not be reached, so an outage
			// does not start refusing birthday presents.
			rdwAnswers.set( bare, answer );
			return answer;
		}

		/* Text whose letters spell nothing: "RF-49-84", "KLSDF12", "XK1234". Those
		   slipped past everything else - the run counting sees a word followed by
		   a number, and the register can only speak for plates that exist, so a
		   made-up one was waved through while looking entirely like a plate.
		   Refused only when NOT ONE letter run of two or more has a vowel in it,
		   so "NR 1 OMA" keeps its OMA. Four digits reading as a year are a
		   birthday or a founding date ("FC 1913"), and exempt the text. */
		const VOWEL = /[AEIOUYÀÂÄÉÈÊËÍÌÎÏÓÒÔÖÚÙÛÜ]/;

		function hasNoWord( bare ) {
			if ( bare.length < 5 || bare.length > 8 ) return false;
			if ( ! /\d/.test( bare ) || ! /[A-Z]/.test( bare ) ) return false;
			const runs = ( bare.match( /[A-Z]+/g ) || [] ).filter( ( r ) => r.length >= 2 );
			if ( ! runs.length ) return false;
			if ( ( bare.match( /\d+/g ) || [] ).some( ( d ) => d.length === 4 && Number( d ) >= 1900 && Number( d ) <= 2099 ) ) return false;
			return ! runs.some( ( r ) => VOWEL.test( r ) );
		}

		async function kentekenProblem( value ) {
			if ( ! textInput?.hasAttribute( 'data-no-kenteken' ) ) return '';
			// A hyphen is how a kenteken is written, and the field refuses it
			// anyway - say so the moment one is typed rather than at the bag.
			if ( value.includes( '-' ) ) return textInput.dataset.msgStreepje;
			const bare = bareText( value );
			if ( hasNoWord( bare ) ) return textInput.dataset.msgKenteken;
			if ( ! KENTEKEN.test( bare ) ) return plateLike( bare ) ? textInput.dataset.msgKenteken : '';
			if ( ( bare.match( /[A-Z]+|\d+/g ) || [] ).length > 2 ) return textInput.dataset.msgKenteken;
			return ( await onRealVehicle( bare ) ) === true ? textInput.dataset.msgKentekenReal : '';
		}

		async function textFieldIsValid() {
			if ( ! textInput ) return true;
			textInput.value = textInput.value.trim();
			const problem = ( await kentekenProblem( textInput.value ) ) || letterProblem( textInput.value );
			if ( problem ) {
				setTextError( problem );
				textInput.focus( { preventScroll: true } );
				textInput.scrollIntoView( { block: 'center', behavior: 'smooth' } );
				return false;
			}
			if ( textInput.validity.valid ) return true;
			if ( textInput.validity.valueMissing && blankBox ) {
				if ( blankConfirmed ) return true;
				showBlank( true );
				// The confirm button takes focus, so keyboard and screen-reader
				// users land on the choice and hear the warning it describes.
				blankBtn?.focus( { preventScroll: true } );
				blankBox.scrollIntoView( { block: 'center', behavior: 'smooth' } );
				return false;
			}
			setTextError( textInput.validity.valueMissing ? textInput.dataset.msgRequired : textInput.dataset.msgInvalid );
			textInput.focus( { preventScroll: true } );
			textInput.scrollIntoView( { block: 'center', behavior: 'smooth' } );
			return false;
		}

		/* Oldtimer rule: a Dutch registration from before 1 January 1978 has
		   two letters and four digits (sidecodes 1-3: XX-99-99, 99-99-XX,
		   99-XX-99), and only those may be pressed on an oldtimer plate. More
		   letters than specs.max_letters is a later registration, so the field
		   refuses it outright - the same way the name plate refuses a kenteken -
		   and the plate never reaches the bag. Until 2026-10-01 this was only a
		   warning next to a consent box; the letters say it, so the box went. */
		const maxLetters = Number( textInput?.dataset.maxLetters || 0 );

		function letterProblem( value ) {
			if ( ! textInput || ! maxLetters ) return '';
			return ( String( value ).match( /\p{L}/gu ) || [] ).length > maxLetters ? textInput.dataset.msgLetters : '';
		}

		/* Some plates are sold in two lengths - an "Aantal tekens" option with
		   "8 tekens" and "11 tekens" on it, the longer one dearer because the
		   characters have to be pressed smaller. The limit then belongs to the
		   chosen length rather than to the product, and the option value carries
		   the number itself, so there is nothing to keep in step by hand.
		   Anything already typed past a newly chosen limit is cut: otherwise a
		   customer could order eleven characters on an eight-character plate. */
		const maxTemplate = textInput?.dataset.msgMax || '';
		let kentekenTimer;

		function chosenLimit() {
			for ( const btn of $$( '.pdp-option__btn.is-selected', pdp ) ) {
				const found = btn.textContent.match( /(\d+)\s*tekens/i );
				if ( found ) return Number( found[ 1 ] );
			}
			return 0;
		}

		function syncTextLimit() {
			if ( ! textInput ) return;
			const max = chosenLimit();
			if ( ! max ) return;
			textInput.maxLength = max;
			if ( textInput.value.length > max ) textInput.value = textInput.value.slice( 0, max );
			if ( textHint && maxTemplate ) {
				textHint.dataset.defaultText = maxTemplate.replace( '#MAX#', max );
				if ( ! textHint.classList.contains( 'is-error' ) ) {
					textHint.textContent = textHint.dataset.defaultText;
				}
			}
		}

		pdp.addEventListener( 'snel:variant', syncTextLimit );
		syncTextLimit();

		textInput?.addEventListener( 'input', () => {
			if ( textInput.hasAttribute( 'aria-invalid' ) || textHint?.classList.contains( 'is-error' ) ) setTextError( '' );
			// Anything typed is a kenteken again, not a blank plate.
			blankConfirmed = false;
			if ( blankBox && ! blankBox.hidden ) showBlank( false );
			// Says so while typing rather than at the bag. Debounced, because the
			// two-run case asks the RDW register and that is a request per answer.
			clearTimeout( kentekenTimer );
			const typed = textInput.value;
			// The hyphen needs no register lookup, so it does not wait for the
			// debounce: it is the single clearest sign someone is typing a plate.
			if ( typed.includes( '-' ) && textInput.dataset.msgStreepje ) setTextError( textInput.dataset.msgStreepje );
			kentekenTimer = setTimeout( async () => {
				const problem = await kentekenProblem( typed );
				if ( problem && textInput.value === typed ) setTextError( problem );
			}, 400 );
			// A third letter needs no lookup either: refused the moment it is typed.
			if ( letterProblem( typed ) ) setTextError( letterProblem( typed ) );
		} );
		// A value the browser restored (back button) is checked like a typed one.
		if ( textInput && letterProblem( textInput.value ) ) setTextError( letterProblem( textInput.value ) );

		/* Consent box (e.g. "my vehicle is from before 1978"): the plate can only
		   be added once it is ticked, and ticking it puts the sentence on the
		   order as a line item property. The add button is disabled until then
		   - like the old theme's click-blocking layer, but as a real disabled
		   button; the phone bar instead scrolls the box into view. */
		const consent = $( '[data-consent-input]', pdp );
		const consentError = $( '[data-consent-error]', pdp );
		const consentBox = consent?.closest( '.pdp-consent' );

		function syncConsent() {
			syncAddButton( findVariant() );
			if ( consent.checked ) {
				if ( consentError ) consentError.textContent = '';
				consentBox?.classList.remove( 'is-error' );
			}
		}

		function consentIsValid() {
			if ( ! consent || consent.checked ) return true;
			if ( consentError ) consentError.textContent = consent.dataset.msgRequired || '';
			consentBox?.classList.add( 'is-error' );
			consent.focus( { preventScroll: true } );
			consent.scrollIntoView( { block: 'center', behavior: 'smooth' } );
			return false;
		}

		if ( consent ) {
			consent.addEventListener( 'change', syncConsent );
			syncConsent();
		}

		async function submitAddToCart( e ) {
			e.preventDefault();
			if ( ! ( await textFieldIsValid() ) || ! consentIsValid() ) return;
			const addBtn = $( '[data-pdp-add-button]', pdp );
			if ( addBtn ) addBtn.classList.add( 'is-loading' );
			if ( blankConfirmed ) blankBtn?.classList.add( 'is-loading' );

			const formData = new FormData( form );
			const mainId = formData.get( 'id' );
			const properties = {};
			for ( const [ key, value ] of formData.entries() ) {
				const m = key.match( /^properties\[(.+)\]$/ );
				if ( m && value ) properties[ m[ 1 ] ] = value;
			}
			// A blank plate: the field is empty, so its property is put on by hand
			// under the field's own name, where the cart and the order expect it.
			if ( blankConfirmed && textInput && ! textInput.value ) {
				const label = ( textInput.name.match( /^properties\[(.+)\]$/ ) || [] )[ 1 ];
				if ( label && blankBox?.dataset.blankProperty ) properties[ label ] = blankBox.dataset.blankProperty;
			}
			const items = [ { id: Number( mainId ), quantity: 1, properties } ];
			$$( '[data-addon]:checked', pdp ).forEach( ( cb ) => {
				items.push( { id: Number( cb.value ), quantity: 1 } );
			} );

			try {
				const res = await fetch( '/cart/add.js', {
					method: 'POST',
					headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
					body: JSON.stringify( { items } ),
				} );
				/* A failing /cart/add.js doesn't always answer in JSON - Shopify
				   serves an HTML page when it throttles a burst of requests - so
				   parsing has to be allowed to fail, or the shopper is shown the
				   parser's own error instead of something they can act on. */
				const data = await res.json().catch( () => null );
				if ( ! res.ok || ! data ) {
					throw new Error( data?.description || data?.message || addBtn?.dataset.msgError || 'Toevoegen lukte niet, probeer het opnieuw.' );
				}
				window.location.assign( window.snelData?.routes?.cart_url || '/cart' );
				return;
			} catch ( err ) {
				const hint = $( '.pdp-text-field__hint', pdp );
				if ( hint ) {
					hint.textContent = err.message;
					hint.classList.add( 'is-error' );
				} else {
					alert( err.message );
				}
			} finally {
				if ( addBtn ) addBtn.classList.remove( 'is-loading' );
				blankBtn?.classList.remove( 'is-loading' );
				blankConfirmed = false;
			}
		}

		blankBtn?.addEventListener( 'click', () => {
			blankConfirmed = true;
			submitAddToCart( { preventDefault() {} } );
		} );


		form?.addEventListener( 'submit', submitAddToCart );

		/* Fixed phone "Add to bag" bar, same rule as mowibrand.com (H&M's
		   "fixedAddToBagContainer"): shown on landing while the real button is
		   below the fold; hidden when a scroll brings the real button into view;
		   shown again only when scrolling back up pushes the real button below
		   the fold. Scrolling on past the real button keeps it hidden. Its
		   button proxies a click to the real one. */
		const stickyBar = $( '[data-sticky-bar]' );
		const realAddBtn = $( '[data-pdp-add-button]', pdp );
		if ( stickyBar && realAddBtn ) {
			const phone = window.matchMedia( '(max-width: 1023px)' );
			const showBar = ( on ) => stickyBar.classList.toggle( 'is-visible', on );
			// Top of the real button, and its bottom plus the bar's own bottom padding.
			const measure = () => {
				const r = realAddBtn.getBoundingClientRect();
				return { top: r.top, end: r.bottom + parseFloat( getComputedStyle( stickyBar ).paddingBottom ), vh: window.innerHeight };
			};
			let lastY = window.scrollY;
			let ticking = false;
			const syncStickyBar = () => {
				ticking = false;
				const y = window.scrollY;
				const down = y > lastY;
				const up = y < lastY;
				lastY = y;
				if ( ! phone.matches ) return;
				const m = measure();
				if ( ( down && m.end < m.vh && m.top > 0 ) || ( up && m.end > 0 && m.end < m.vh ) ) {
					showBar( false );
				} else if ( up && m.end > m.vh ) {
					showBar( true );
				}
			};
			window.addEventListener( 'scroll', () => {
				if ( ! ticking ) {
					ticking = true;
					requestAnimationFrame( syncStickyBar );
				}
			}, { passive: true } );
			if ( phone.matches && measure().end > window.innerHeight ) showBar( true );
			// The real button is disabled until the consent box is ticked, so a tap
			// here has to send the customer to that box instead of doing nothing.
			$( '[data-sticky-bar-btn]', stickyBar )?.addEventListener( 'click', () => {
				if ( ! consentIsValid() ) return;
				realAddBtn.click();
			} );
		}

		/* Gallery counter (mobile swipe). */
		const galleryTrack = $( '.gallery__track', pdp );
		const galleryCounter = $( '[data-gallery-counter]', pdp );
		if ( galleryTrack && galleryCounter ) {
			galleryTrack.addEventListener( 'scroll', () => {
				const items = $$( '.gallery__item', galleryTrack );
				const idx = Math.round( galleryTrack.scrollLeft / galleryTrack.clientWidth );
				galleryCounter.textContent = `${ Math.min( idx + 1, items.length ) } / ${ items.length }`;
			}, { passive: true } );
		}
	}

	/* ------------------------------------------------------------------
	 * Bag (cart) page: quantity stepper + discount code
	 * ---------------------------------------------------------------- */
	const bag = $( '[data-bag]' );
	if ( bag ) {
		function updateCartCount( count ) {
			$$( '.snel-cart-count' ).forEach( ( el ) => {
				el.textContent = count;
				el.hidden = count === 0;
			} );
		}

		async function changeLine( line, quantity ) {
			const res = await fetch( '/cart/change.js', {
				method: 'POST',
				headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
				body: JSON.stringify( { line, quantity } ),
			} );
			const cart = await res.json();
			updateCartCount( cart.item_count );
			window.location.reload();
		}

		let debounceTimer = null;
		$$( '[data-bag-line]', bag ).forEach( ( row ) => {
			const line = Number( row.dataset.bagLine );
			const qtyEl = $( '[data-qty]', row );
			let qty = Number( qtyEl.textContent );

			// The staffel note under the stepper belongs to quantity 1 only (cart.liquid).
			const staffelNote = $( '[data-staffel-note]', row );
			$( '[data-qty-minus]', row )?.addEventListener( 'click', () => {
				qty = Math.max( 0, qty - 1 );
				qtyEl.textContent = qty;
				if ( staffelNote ) staffelNote.hidden = qty !== 1;
				clearTimeout( debounceTimer );
				debounceTimer = setTimeout( () => changeLine( line, qty ), 450 );
			} );
			$( '[data-qty-plus]', row )?.addEventListener( 'click', () => {
				qty += 1;
				qtyEl.textContent = qty;
				if ( staffelNote ) staffelNote.hidden = qty !== 1;
				clearTimeout( debounceTimer );
				debounceTimer = setTimeout( () => changeLine( line, qty ), 450 );
			} );
		} );

		$$( '[data-upsell-variant]', bag ).forEach( ( select ) => {
			const item = select.closest( '.bag-upsell__item' );
			select.addEventListener( 'change', () => {
				$( '[data-upsell-add]', item ).dataset.upsellAdd = select.value;
				$( '[data-upsell-price]', item ).textContent = select.selectedOptions[ 0 ].dataset.price;
			} );
		} );

		$$( '[data-upsell-add]', bag ).forEach( ( btn ) => {
			btn.addEventListener( 'click', async () => {
				btn.disabled = true;
				btn.classList.add( 'is-loading' );
				try {
					const res = await fetch( '/cart/add.js', {
						method: 'POST',
						headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
						body: JSON.stringify( { items: [ { id: Number( btn.dataset.upsellAdd ), quantity: 1 } ] } ),
					} );
					if ( ! res.ok ) throw new Error( 'add failed' );
					window.location.reload();
				} catch ( err ) {
					btn.disabled = false;
					btn.classList.remove( 'is-loading' );
					btn.textContent = btn.dataset.retry;
				}
			} );
		} );

		$( '[data-discount-form]', bag )?.addEventListener( 'submit', async ( e ) => {
			e.preventDefault();
			const code = $( '[data-discount-input]', bag )?.value.trim();
			if ( ! code ) return;
			await fetch( '/cart/update.js', {
				method: 'POST',
				headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
				body: JSON.stringify( { discount: code } ),
			} );
			window.location.reload();
		} );
	}

	window.snelShop = { openFilters, closeFilters };
} )();
